const isChannelNumber = (value: unknown): boolean =>
	typeof value === 'number' && !Number.isNaN(value) && value >= 0 && value <= 100;

/** Legacy smokeAlarm sent smoke/alarm as the only field without a leading channel. */
const coerceSmokeAlarmValues = (values: unknown[]): unknown[] => {
	if (values.length === 0) {
		return [0, true];
	}
	if (values.length === 1 && !isChannelNumber(values[0])) {
		return [0, values[0]];
	}
	if (values.length === 1 && isChannelNumber(values[0])) {
		return [values[0], true];
	}
	return values;
};

/** Legacy inputState used [channel, reason]; new format adds component as a third field. */
const coerceInputStateValues = (values: unknown[]): unknown[] => {
	if (values.length === 2) {
		return [values[0], values[1], undefined];
	}
	return values;
};

/** Legacy DwBleState omitted reason and used door/rotation field names in v1 arrays. */
const isLegacyDwBlePayload = (values: unknown[]): boolean =>
	(values[0] === undefined || values[0] === null) && values.length >= 2;

const coerceDwBleStateValues = (values: unknown[]): unknown[] => {
	if (isLegacyDwBlePayload(values)) {
		return [undefined, values[1], values[2], values[3], values[4]];
	}
	return values.length >= 5 ? values.slice(0, 5) : values;
};

/** Positional layout unchanged; legacy first field was named vibration instead of state. */
const coerceVibrationDistanceValues = (values: unknown[]): unknown[] =>
	values.length >= 3 ? values.slice(0, 3) : values;

const PRESENCE_UNIFIED_FIELD_COUNT = 8;

/** Legacy presence used track/no_track/presence_detected/presence_zone/illumination slots. */
const isLegacyPresencePayload = (values: unknown[]): boolean => {
	if (values.length > 3) {
		return true;
	}
	if (values.length === 0) {
		return false;
	}
	const [first, second, third] = values;
	if (typeof first === 'boolean' || typeof second === 'boolean') {
		return true;
	}
	if (typeof third === 'number' && !Number.isNaN(third)) {
		return false;
	}
	if (typeof first === 'string' && (first.includes(':') || first.startsWith('presence'))) {
		return false;
	}
	return typeof third === 'boolean';
};

const coercePresenceValues = (values: unknown[]): unknown[] => {
	const unified = Array(PRESENCE_UNIFIED_FIELD_COUNT).fill(undefined);

	if (!isLegacyPresencePayload(values)) {
		unified[0] = values[0];
		unified[1] = values[1];
		unified[2] = values[2];
		return unified;
	}

	values.slice(0, 5).forEach((value, index) => {
		unified[3 + index] = value;
	});
	return unified;
};

/**
 * Normalizes v1 positional payloads to the current EVENT_KEYS layout while keeping
 * legacy array shapes readable when newer fields are absent.
 */
export const coerceEventLogPayloadValues = (eventId: number, values: unknown[]): unknown[] => {
	switch (eventId) {
		case 12:
			return coerceSmokeAlarmValues(values);
		case 13:
			return coerceInputStateValues(values);
		case 14:
			return coerceDwBleStateValues(values);
		case 19:
			return coerceVibrationDistanceValues(values);
		case 20:
			return coercePresenceValues(values);
		default:
			return values;
	}
};
